import * as React from 'react';
import { observer } from 'mobx-react';
import { Employee } from 'nomst/Stores/Decision/employee';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { AmountCode } from 'typings/enums';
import { Amount } from 'nomst/Stores/Decision/amount';
import PayRange from 'nomst/Components/Decision/payRange';
import PayText from 'components/Common/payText';
import CompViewHelper from 'sharedLogic/compViewHelper';
import './compensationSummary.scss';

export interface ICompensationSummaryProps {
    employee: Employee;
    configuration: Configuration;
}

export class CompensationSummary extends React.Component<ICompensationSummaryProps> {
    constructor(props: ICompensationSummaryProps) {
        super(props);

        this.renderAmountText = this.renderAmountText.bind(this);
    }

    render(): React.ReactNode {
        const { employee, configuration } = this.props;
        return (
            <div className="compensation-summary">
                <h3 className="compensation-summary__title">Comp Summary</h3>
                {this.renderPayRange()}
                <table className="summary-table">
                    <tbody>
                        {this.renderPayText(
                            'summary-table__current-pay',
                            'Current Pay',
                            this.currentPayText,
                            this.annualizedCurrentPayText)}
                        {employee.amounts
                            .filter(amount => CompViewHelper.shouldRenderAmount(amount, configuration)
                                && amount.updateNewPay)
                            .map(this.renderAmountText)}
                        {this.renderPayText(
                            'summary-table__subtotal summary-table__subtotal--new-pay',
                            'New Pay',
                            this.newPayText,
                            this.annualizedNewPayText)}
                        {this.renderMayAffectTccComponents(true)}
                        {this.renderTcc()}
                        {this.renderMayAffectTccComponents(false)}
                        {this.renderStock()}
                    </tbody>
                </table>
            </div>
        );
    }

    private renderPayRange(): React.ReactNode {
        const { employee, configuration } = this.props;
        if (!configuration.payRangeConfiguration.showPayRange) {
            return null;
        }

        const payRangeClass = configuration.ratingConfiguration?.showPerformanceRating ?
            'range-with-perf-rating' :
            'range-no-perf-rating';

        return (
            <div className="perf-pay-range">
                <PayRange
                    payRange={employee.range}
                    payAmount={employee.basePayForPayRange}
                    configuration={configuration}
                    className={payRangeClass}
                    chartClassName="compensation-summary__pay-range"
                    barHeight={8}
                    pointerHeight={15}
                    useCompactPayRange={false}
                />
            </div>
        );
    }

    private renderPayText(
        rowClassName: string,
        label: string,
        payText: string,
        annualPayText: string): React.ReactNode {
        const { employee } = this.props;
        return (
            <tr className={rowClassName}>
                <th className="label">{label}</th>
                <td>
                    <PayText
                        payText={payText}
                        annualPayText={annualPayText}
                        isHourly={employee.isHourly}
                    />
                </td>
            </tr>
        );
    }

    private renderAmountText(amount: Amount): React.ReactNode {
        const { configuration } = this.props;
        if (!amount) {
            return null;
        }

        const cycleBudgetField = configuration.getCycleBudgetField(amount.amountCode);
        return (
            <tr className={`summary-table__${amount.amountCode}`} key={amount.amountCode}>
                <th className="label">{cycleBudgetField.title}</th>
                <td data-mf-replace-inner="***">
                    {this.getAmountText(amount)}
                    <br />
                    {this.renderProration(amount)}
                </td>
            </tr>
        );
    }

    private renderMayAffectTccComponents(doesAffectTcc: boolean): React.ReactNode {
        const { configuration, employee } = this.props;
        if (!configuration.teamPageViews.tcc) {
            return null;
        }

        const tccComponents = employee.amounts
            .filter(amount => CompViewHelper.shouldTccIncludeAmount(amount, configuration, doesAffectTcc))
            .map(this.renderAmountText);

        const nonCycleAmounts = CompViewHelper.getNonCycleAmounts(employee, configuration, doesAffectTcc)
            .map(nonCycleAmount => this.renderTccComponent(
                nonCycleAmount.className,
                nonCycleAmount.title,
                nonCycleAmount.amount,
                nonCycleAmount.shouldRender));
        tccComponents.push(...nonCycleAmounts);

        return tccComponents;
    }

    private renderTccComponent(
        componentClassName: string,
        componentTitle: string,
        componentValue: number,
        doesComponentAffectTcc: boolean): React.ReactNode {
        if (!doesComponentAffectTcc) {
            return null;
        }

        return (
            <tr key={componentTitle} className={`summary-table__${componentClassName} tcc-component`}>
                <th className="label">{componentTitle}</th>
                <td data-mf-replace-inner="***">
                    {this.getTccComponentText(componentValue)}
                </td>
            </tr>
        );
    }

    private renderTcc(): React.ReactNode {
        const { configuration } = this.props;

        if (configuration.teamPageViews.tcc === false) {
            return null;
        }

        return (
            <tr className="summary-table__subtotal summary-table__subtotal--tcc">
                <th className="label">TCC</th>
                <td data-mf-replace-inner="***">{this.totalCombinedCompensationText}</td>
            </tr>
        );
    }

    private renderStock(): React.ReactNode {
        const { configuration, employee } = this.props;
        if (!employee.stockAmount || !configuration.teamPageViews.isAmountUsedInCompSummary(AmountCode.Stock)) {
            return null;
        }

        return (
            <tr className="summary-table__stock">
                <th className="label">Stock</th>
                <td data-mf-replace-inner="***">
                    {CompViewHelper.getStockText(employee.stockAmount, configuration.format)}
                </td>
            </tr>
        );
    }

    private getAmountText(amount: Amount): string {
        const { configuration, employee } = this.props;
        if (amount.value <= 0) {
            return configuration.format.formatCurrencyValue(
                amount.currentIsoCurrencyCode,
                0,
                employee.isHourly && amount.updateNewPay);
        }

        const increaseText = configuration.format.formatCurrencyValue(
            amount.currentIsoCurrencyCode,
            amount.convertedAmount,
            employee.isHourly && amount.updateNewPay);

        const percentage = CompViewHelper.getAmountPercentage(amount, employee);
        const percentageText = configuration.format.formatPercentage(percentage);

        return `${increaseText} (${percentageText})`;
    }

    private renderProration(amount: Amount): React.ReactNode {
        if (!amount.isProrated || amount.isProrationOverride) {
            return null;
        }

        if (amount.isEligible === false) {
            return null;
        }

        const { configuration } = this.props;
        const prorationPercentageText = configuration.format.formatPercentage(
            amount.proratedPercentage);
        const className = `summary-table__prorated-percentage summary-table__prorated-percentage--${amount.amountCode}`;
        return (
            <span className={className}>
                Proration <b>{prorationPercentageText}</b>
            </span>
        );
    }

    private getTccComponentText(tccComponentValue: number): string {
        const { configuration, employee } = this.props;
        if (!tccComponentValue) {
            tccComponentValue = 0;
        }

        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            tccComponentValue,
            false);
    }

    private get currentPayText(): string {
        const { employee, configuration } = this.props;
        const convertedCurrentPay = employee.isHourly ?
            employee.convertedCurrentPay :
            employee.convertedAnnualizedCurrentPay;

        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            convertedCurrentPay,
            employee.isHourly);
    }

    private get annualizedCurrentPayText(): string {
        const { employee, configuration } = this.props;
        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            employee.convertedAnnualizedCurrentPay,
            false);
    }

    private get newPayText(): string {
        const { employee, configuration } = this.props;
        const convertedNewPay = employee.isHourly ?
            employee.convertedNewPay :
            employee.convertedAnnualizedNewPay;

        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            convertedNewPay,
            employee.isHourly);
    }

    private get annualizedNewPayText(): string {
        const { employee, configuration } = this.props;
        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            employee.convertedAnnualizedNewPay,
            false);
    }

    private get totalCombinedCompensationText(): string {
        const { employee, configuration } = this.props;
        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            employee.convertedTotalCombinedCompensation,
            false);
    }
}

export default observer(CompensationSummary);